import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DocumentNode, useMutation, useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { socketIo } from 'context/socket';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { downloadUrl } from 'utils/file';

/** The backend gives up after five minutes; wait a little longer before assuming the event was lost. */
const EXPORT_TIMEOUT_MS = 6 * 60 * 1000;

export type AsyncExportEventData = {
    userId: string;
    fileUrl?: string;
    fileName?: string;
    rowCount?: number;
    errorMessage?: string;
} & Record<string, unknown>;

type AsyncExportEvent = { data: AsyncExportEventData };

type UseAsyncExportOptions = {
    /** Mutation that acknowledges the request; the file arrives later through the socket. */
    mutation: DocumentNode;
    variables: Record<string, unknown>;
    events: { ready: string; error: string };
    /** Narrows the user's events to this page's scope (a clinic, a batch, ...). Must be stable. */
    isForThisScope: (data: AsyncExportEventData) => boolean;
    /** Shown instead of downloading a header-only workbook. */
    emptyMessage: string;
};

/**
 * Backend-side Excel exports: the mutation only acknowledges the request, the backend
 * builds the workbook in the background and announces a presigned download link through
 * a socket event. The hook keeps the loader open until that event (or a timeout) arrives.
 */
export const useAsyncExport = ({
    mutation,
    variables,
    events,
    isForThisScope,
    emptyMessage,
}: UseAsyncExportOptions) => {
    const { t: translate } = useTranslation(['users']);
    const socket = useContext(socketIo.getSocketContext());
    const currentUser = useReactiveVar(currentUserVar);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isExporting, setIsExporting] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const stopWaiting = useCallback(() => {
        clearTimeout(timeoutRef.current);
        setIsExporting(false);
    }, []);

    const [requestMutation] = useMutation(mutation, {
        onError(error) {
            stopWaiting();
            showBoundary(error);
        },
    });

    const requestExport = useCallback(() => {
        setIsExporting(true);
        timeoutRef.current = setTimeout(() => {
            stopWaiting();
            setAppToasterContent({ isOpen: true, severity: 'error', message: translate('users:EXPORT_TIMEOUT') });
        }, EXPORT_TIMEOUT_MS);
        requestMutation({ variables });
    }, [variables, requestMutation, setAppToasterContent, stopWaiting, translate]);

    useEffect(() => {
        const isMine = ({ data }: AsyncExportEvent) => data.userId === currentUser._id && isForThisScope(data);

        const handleReady = (event: AsyncExportEvent) => {
            if (!isMine(event) || !event.data.fileUrl) {
                return;
            }
            stopWaiting();
            if (event.data.rowCount === 0) {
                setAppToasterContent({ isOpen: true, severity: 'info', message: emptyMessage });
                return;
            }
            downloadUrl(event.data.fileName || 'export.xlsx', event.data.fileUrl);
        };

        const handleError = (event: AsyncExportEvent) => {
            if (!isMine(event)) {
                return;
            }
            stopWaiting();
            setAppToasterContent({ isOpen: true, severity: 'error', message: translate('users:EXPORT_FAILED') });
        };

        socket.on(events.ready, handleReady);
        socket.on(events.error, handleError);
        return () => {
            // Remove only these listeners; other components share the same socket.
            socket.off(events.ready, handleReady);
            socket.off(events.error, handleError);
        };
    }, [
        socket,
        currentUser._id,
        events.ready,
        events.error,
        isForThisScope,
        emptyMessage,
        stopWaiting,
        setAppToasterContent,
        translate,
    ]);

    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    return { requestExport, isExporting };
};
